import React, { useEffect } from 'react';
import { Grid, makeStyles, Theme, Slider, Button, Dialog } from '@material-ui/core';
import Cropper from 'react-easy-crop';
import getCroppedImg from './cropImage';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles<Theme>((theme) => ({
	root: {
		display: 'flex',
		alignItems: 'center',
		minHeight: 'max-content',
		[theme.breakpoints.down(824)]: {
			maxWidth: '510px',
		},
		[theme.breakpoints.down(600)]: {
			maxWidth: '340px',
		},
	},
	dialogButtonContainer: {
		margin: '30px auto 10px auto',
		maxWidth: '450px',
		width: '100%',
	},
	cropContainer: {
		width: '670px',
		height: '408px',
		position: 'relative',
		display: 'flex',
		[theme.breakpoints.down(824)]: {
			height: '300px',
			width: '500px',
		},
		[theme.breakpoints.down(600)]: {
			height: '200px',
			width: '335px',
		},
	},
	buttonSize: {
		minWidth: '150px',
		[theme.breakpoints.down(370)]: {
			minWidth: '125px',
		},
	},
}));

interface ICropImageDialogProps {
	isOpen: boolean;
	setOpen: (state: boolean) => void;
	pictureUrl: File | null;
	setCroppedImage?: (url: string | null, path: Blob | null) => void;
	imageDimensions: { width: number; height: number };
}
const CropImageDialog: React.FC<ICropImageDialogProps> = (props) => {
	const classes = useStyles(props);
	const { t } = useTranslation();

	//crop values
	const [imageSrc, setImageSrc] = React.useState<string | null>(null);
	const [crop, setCrop] = React.useState({ x: 0, y: 0 });
	const [croppedAreaPixels, setCroppedAreaPixels] = React.useState(undefined);
	const [zoom, setZoom] = React.useState(1);
	const [isCropping, setIsCropping] = React.useState(false);

	async function initCropData() {
		const file = props.pictureUrl;
		let imageDataUrl = await readFile(file);
		let imageString = JSON.stringify(imageDataUrl);

		setImageSrc(imageString);
		setZoom(1);
		setCrop({ x: 0, y: 0 });
	}

	useEffect(() => {
		initCropData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	function readFile(file) {
		return new Promise((resolve) => {
			const reader = new FileReader();
			reader.addEventListener('load', () => resolve(reader.result), false);
			reader.readAsDataURL(file);
		});
	}

	function onCropChange(crop) {
		setCrop({ x: crop.x, y: crop.y });
	}

	const onCropComplete = (croppedArea, croppedAreaPixels) => {
		setCroppedAreaPixels(croppedAreaPixels);
	};

	const onZoomChange = (zoom) => {
		setZoom(zoom);
	};

	const showResult = async () => {
		try {
			setIsCropping(true);
			const croppedImage = await getCroppedImg(JSON.parse(imageSrc!), croppedAreaPixels);

			props.setCroppedImage!(URL.createObjectURL(croppedImage), croppedImage);
			props.setOpen(false);

			setIsCropping(false);
		} catch (e) {
			console.error(e);
			setIsCropping(false);
		}
	};

	return (
		<Dialog open={props.isOpen} maxWidth={'md'} onClose={() => props.setOpen(false)}>
			<Grid container item direction='column' className={classes.root}>
				<div className={classes.cropContainer}>
					<Cropper
						image={JSON.parse(imageSrc!)}
						crop={crop}
						zoom={zoom}
						aspect={props.imageDimensions.width / props.imageDimensions.height}
						onCropChange={onCropChange}
						onCropComplete={onCropComplete}
						onZoomChange={onZoomChange}
					/>
				</div>
				<div className='controls' style={{ width: '60%', marginTop: '15px', marginBottom: '30px' }}>
					<Slider value={zoom} min={1} max={3} step={0.1} aria-labelledby='Zoom' onChange={(e, zoom) => onZoomChange(zoom)} />
				</div>
				<div className={classes.cropButtonContainer} style={{ display: 'flex', marginBottom: '25px' }}>
					<Button color='primary' variant='outlined' onClick={showResult} className={classes.buttonSize} disabled={isCropping}>
						{t('common.save')}
					</Button>
				</div>
			</Grid>
		</Dialog>
	);
};

export default CropImageDialog;
